import { Effect, put, select, takeLeading } from 'redux-saga/effects'

import { POI } from 'generated/mos/poi'
import {
  GetPOIRequest,
  GetPOIResponse,
  ListPOIsRequest,
  ListPOIsResponse,
  ListPOITypesRequest,
  ListPOITypesResponse,
} from 'generated/mos/poiaccess'
import {
  CreatePOIRequest,
  CreatePOIResponse,
  DeletePOIRequest,
  DeletePOIResponse,
  UpdatePOIRequest,
  UpdatePOIResponse,
} from 'generated/mos/poimanagement'
import { failureStatusMessage, unaryGRPC } from 'helpers/sagas'

import { POIsActions, actionCreators } from './actions'

const poisTakeLeading = <TActionKey extends keyof POIsActions>(
  pattern: TActionKey,
  worker: (action: POIsActions[TActionKey]) => any,
) => {
  return takeLeading(pattern, worker);
}

const listPOITypesSaga = poisTakeLeading(
  'poisListPOITypesRequest',
  function* (action: POIsActions['poisListPOITypesRequest']) {
    try {
      const resp = yield* unaryGRPC<
        ListPOITypesRequest.Entity,
        ListPOITypesResponse.Entity
      >(
        'mos.poi_access.POIAccess/ListPOITypes',
        ListPOITypesRequest.defaults,
        ListPOITypesRequest.codec,
        ListPOITypesResponse.codec,
      );
      yield put(actionCreators.poisListPOITypesSuccess(resp.poiTypes));
    } catch (err) {
      yield put(actionCreators.poisListPOITypesFailure(failureStatusMessage(err.message)));
    }
  }
)

const listPOIsSaga = poisTakeLeading(
  'poisListPOIsRequest',
  function* (action: POIsActions['poisListPOIsRequest']) {
    try {
      const resp = yield* unaryGRPC<
        ListPOIsRequest.Entity,
        ListPOIsResponse.Entity
      >(
        'mos.poi_access.POIAccess/ListPOIs',
        ListPOIsRequest.defaults,
        ListPOIsRequest.codec,
        ListPOIsResponse.codec,
      );
      yield put(actionCreators.poisListPOIsSuccess(resp.pois));
    } catch (err) {
      yield put(actionCreators.poisListPOIsFailure(failureStatusMessage(err.message)));
    }
  
  }
)

const getPOISaga = poisTakeLeading(
  'poisGetPOIRequest',
  function* (action: POIsActions['poisGetPOIRequest']) {
    const request: GetPOIRequest.Entity = {
      type: GetPOIRequest.refName,
      poiRef: action.ref,
    }

    try {
      const resp = yield* unaryGRPC<
        GetPOIRequest.Entity,
        GetPOIResponse.Entity
      >(
        'mos.poi_access.POIAccess/GetPOI',
        request,
        GetPOIRequest.codec,
        GetPOIResponse.codec,
      );
      yield put(actionCreators.poisGetPOISuccess(resp));
    } catch (err) {
      yield put(actionCreators.poisGetPOIFailure(
        action.ref,
        failureStatusMessage(err.message),
      ));
    }
  }
)

const createPOISaga = poisTakeLeading(
  'poisCreatePOIRequest',
  function* (action: POIsActions['poisCreatePOIRequest']) {
    const { payload } = action;
    const request: CreatePOIRequest.Entity = {
      type: CreatePOIRequest.refName,
      ...payload
    }

    try {
      const entity = yield* unaryGRPC<
        CreatePOIRequest.Entity,
        CreatePOIResponse.Entity
      >(
        'mos.poi_management.POIManagement/CreatePOI',
        request,
        CreatePOIRequest.codec,
        CreatePOIResponse.codec,
      );
      yield put(actionCreators.poisCreatePOISuccess(
        POI.mustRef(entity.poi!.ref)
      ));
    } catch (err) {
      yield put(actionCreators.poisCreatePOIFailure(failureStatusMessage(err.message)));
    }
  }
)

const updatePOISaga = poisTakeLeading(
  'poisUpdatePOIRequest',
  function* (action: POIsActions['poisUpdatePOIRequest']) {
    const { payload } = action
    const request: UpdatePOIRequest.Entity = {
      type: UpdatePOIRequest.refName,
      ...payload,
      poiRef: payload.poiRef,
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: ['name', 'locations', 'poiTypeRef', 'visitorInfo'],
      }
    }
    try {
      const resp = yield* unaryGRPC<
        UpdatePOIRequest.Entity,
        UpdatePOIResponse.Entity
      >(
        'mos.poi_management.POIManagement/UpdatePOI',
        request,
        UpdatePOIRequest.codec,
        UpdatePOIResponse.codec,
      );
      yield put(actionCreators.poisUpdatePOISuccess(resp));
      yield put(actionCreators.poisGetPOIRequest(POI.mustRef(resp.poi!.ref)))
    } catch (err) {
      yield put(actionCreators.poisUpdatePOIFailure(
        POI.mustRef(payload.poiRef),
        failureStatusMessage(err.message)
      ));
    }
  }
)

const deletePOISaga = poisTakeLeading(
  'poisDeletePOIRequest',
  function* (action: POIsActions['poisDeletePOIRequest']) {
    const { pois: { poiEdit } } = yield select()
    const request = {
      type: DeletePOIRequest.refName,
      poiRef: poiEdit.ref,
    }

    try {
      const entity = yield* unaryGRPC<
        DeletePOIRequest.Entity,
        DeletePOIResponse.Entity
      >(
        'mos.poi_management.POIManagement/DeletePOI',
        request,
        DeletePOIRequest.codec,
        DeletePOIResponse.codec,
      );
      yield put(actionCreators.poisDeletePOISuccess(POI.mustRef(entity.ref)));
      // reload to be safe.
      yield put(actionCreators.poisListPOIsRequest())
    } catch (err) {
      yield put(actionCreators.poisDeletePOIFailure(action.poiRef, failureStatusMessage(err.message)));
    }
  }
)

export const poisRootSaga: ReadonlyArray<Effect> = [
  listPOITypesSaga,
  listPOIsSaga,
  getPOISaga,
  createPOISaga,
  updatePOISaga,
  deletePOISaga,
]
